import { ExclamationCircleOutlined, SearchOutlined, ClearOutlined } from "@ant-design/icons";
import { Button, Card, Input, Space, Table, Tag, Tooltip, Typography, message } from "antd";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

import { AppState } from "../../../store";
import { useApiContext } from "../../../utils";

import { AlertDebugPanel } from "./AlertDebugPanel";
import { createSmartAlertsDebugApi } from "./api";
import { AlertItem } from "./types";

const { Text } = Typography;

const PageWrapper = styled.div`
  padding: 24px;
  max-width: 1400px;
`;

const PageHeader = styled.div`
  margin-bottom: 20px;
`;

const PageTitle = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 4px;
`;

const PageSubtitle = styled.div`
  font-size: 13px;
  color: #888;
`;

const FilterCard = styled(Card)`
  margin-bottom: 16px;
  border-radius: 8px;

  .ant-card-body {
    padding: 16px 20px;
  }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
  flex-wrap: wrap;
`;

const FilterField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 180px;
  max-width: 260px;
`;

const FilterLabel = styled.div`
  font-size: 12px;
  color: #666;
  font-weight: 500;
`;

const ResultMeta = styled.div`
  margin-bottom: 8px;
  font-size: 13px;
  color: #888;
`;

function formatDateTime(iso?: string): string {
  if (!iso) {
    return "-";
  }
  return new Date(iso).toLocaleString();
}

function timeAgo(iso?: string): string {
  if (!iso) {
    return "";
  }
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) {
    return "just now";
  }
  if (mins < 60) {
    return `${mins}m ago`;
  }
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) {
    return `${hrs}h ago`;
  }
  return `${Math.floor(hrs / 24)}d ago`;
}

export const SmartAlertsDebug: React.FC = () => {
  const { isCoeffAdmin } = useSelector(({ app }: AppState) => ({
    isCoeffAdmin: app.isCoeffAdmin,
  }));
  const apiContext = useApiContext();

  const [emailFilter, setEmailFilter] = useState("");
  const [alertIdFilter, setAlertIdFilter] = useState("");
  const [spreadsheetIdFilter, setSpreadsheetIdFilter] = useState("");
  const [runIdFilter, setRunIdFilter] = useState("");

  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = useCallback(async () => {
    if (!apiContext?.DEPRECATED_api) {
      return;
    }
    const api = createSmartAlertsDebugApi(apiContext.DEPRECATED_api);
    setLoading(true);
    setHasSearched(true);
    try {
      const response = await api.searchAlerts({
        email: emailFilter.trim() || undefined,
        smart_alert_id: alertIdFilter.trim() || undefined,
        spreadsheet_id: spreadsheetIdFilter.trim() || undefined,
        run_id: runIdFilter.trim() || undefined,
        limit: 100,
        offset: 0,
      });
      setAlerts(response.alerts || []);
      setTotal(response.total ?? 0);
      setExpandedRowKeys([]);
    } catch (error: unknown) {
      const err = error as { error?: { message?: string } };
      message.error(err?.error?.message || "Failed to search alerts");
    } finally {
      setLoading(false);
    }
  }, [apiContext, emailFilter, alertIdFilter, spreadsheetIdFilter, runIdFilter]);

  // Load recent alerts on page load
  useEffect(() => {
    handleSearch();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClear = () => {
    setEmailFilter("");
    setAlertIdFilter("");
    setSpreadsheetIdFilter("");
    setRunIdFilter("");
    setAlerts([]);
    setTotal(0);
    setExpandedRowKeys([]);
    setHasSearched(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedRowKeys(prev => (prev.includes(id) ? prev.filter(k => k !== id) : [...prev, id]));
  };

  if (!isCoeffAdmin) {
    return <div>Unauthorized</div>;
  }

  const columns = [
    {
      title: "Alert",
      key: "alert",
      width: 260,
      render: (_: unknown, record: AlertItem) => (
        <div>
          <div style={{ fontWeight: 500, marginBottom: 2 }}>
            {record.name || (
              <Text type="secondary" style={{ fontStyle: "italic" }}>
                Unnamed
              </Text>
            )}
          </div>
          <Text
            copyable={{ text: record.smart_alert_id }}
            style={{ fontFamily: "monospace", fontSize: 11, color: "#888" }}
          >
            {record.smart_alert_id.slice(0, 8)}…
          </Text>
        </div>
      ),
    },
    {
      title: "Owner",
      key: "owner",
      width: 220,
      render: (_: unknown, record: AlertItem) => (
        <Tooltip title={record.email}>
          <Text style={{ fontSize: 13 }}>
            {record.email.length > 28 ? `${record.email.slice(0, 28)}…` : record.email}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: "Spreadsheet",
      dataIndex: "spreadsheet_id",
      key: "spreadsheet_id",
      width: 160,
      render: (id: string | undefined) => {
        if (!id) {
          return <Text type="secondary">—</Text>;
        }
        return (
          <Tooltip title={id}>
            <Text style={{ fontFamily: "monospace", fontSize: 11, color: "#555" }}>
              {id.length > 18 ? `${id.slice(0, 18)}…` : id}
            </Text>
          </Tooltip>
        );
      },
    },
    {
      title: "Health",
      key: "health",
      width: 130,
      render: (_: unknown, record: AlertItem) => {
        if (record.deleted_dt) {
          return <Tag color="default">Deleted</Tag>;
        }
        const failures = record.consecutive_failures ?? 0;
        if (failures > 0) {
          return (
            <Tag color="red" icon={<ExclamationCircleOutlined />}>
              {failures} failure{failures !== 1 ? "s" : ""}
            </Tag>
          );
        }
        if (record.is_active === false) {
          return <Tag color="orange">Inactive</Tag>;
        }
        return <Tag color="green">Healthy</Tag>;
      },
    },
    {
      title: "Last Run",
      dataIndex: "last_run_dt",
      key: "last_run_dt",
      width: 160,
      render: (val: string | undefined) => {
        if (!val) {
          return <Text type="secondary">Never</Text>;
        }
        return (
          <Tooltip title={formatDateTime(val)}>
            <Text style={{ fontSize: 13 }}>{timeAgo(val)}</Text>
          </Tooltip>
        );
      },
    },
    {
      title: "Created",
      dataIndex: "created_dt",
      key: "created_dt",
      width: 130,
      render: (val: string | undefined) => {
        if (!val) {
          return <Text type="secondary">—</Text>;
        }
        return (
          <Tooltip title={formatDateTime(val)}>
            <Text style={{ fontSize: 13, color: "#888" }}>{timeAgo(val)}</Text>
          </Tooltip>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 90,
      render: (_: unknown, record: AlertItem) => {
        const expanded = expandedRowKeys.includes(record.smart_alert_id);
        return (
          <Button
            size="small"
            type={expanded ? "primary" : "default"}
            ghost={expanded}
            onClick={() => toggleExpand(record.smart_alert_id)}
          >
            {expanded ? "Hide Runs" : "View Runs"}
          </Button>
        );
      },
    },
  ];

  return (
    <PageWrapper>
      <PageHeader>
        <PageTitle>Smart Alerts Debug</PageTitle>
        <PageSubtitle>
          Search alerts by owner, ID, or spreadsheet. Drill into run history and LangSmith traces.
        </PageSubtitle>
      </PageHeader>

      <FilterCard>
        <FilterRow>
          <FilterField>
            <FilterLabel>Email</FilterLabel>
            <Input
              placeholder="user@example.com"
              value={emailFilter}
              onChange={e => setEmailFilter(e.target.value)}
              onKeyDown={handleKeyDown}
              allowClear
            />
          </FilterField>
          <FilterField>
            <FilterLabel>Alert ID</FilterLabel>
            <Input
              placeholder="UUID"
              value={alertIdFilter}
              onChange={e => setAlertIdFilter(e.target.value)}
              onKeyDown={handleKeyDown}
              allowClear
            />
          </FilterField>
          <FilterField>
            <FilterLabel>Spreadsheet ID</FilterLabel>
            <Input
              placeholder="File or sheet ID"
              value={spreadsheetIdFilter}
              onChange={e => setSpreadsheetIdFilter(e.target.value)}
              onKeyDown={handleKeyDown}
              allowClear
            />
          </FilterField>
          <FilterField>
            <FilterLabel>Run ID</FilterLabel>
            <Input
              placeholder="UUID"
              value={runIdFilter}
              onChange={e => setRunIdFilter(e.target.value)}
              onKeyDown={handleKeyDown}
              allowClear
            />
          </FilterField>
          <Space style={{ paddingBottom: 1 }}>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
            >
              Search
            </Button>
            <Button icon={<ClearOutlined />} onClick={handleClear}>
              Clear
            </Button>
          </Space>
        </FilterRow>
      </FilterCard>

      {hasSearched && (
        <ResultMeta>
          {loading ? "Searching…" : `${total} alert${total !== 1 ? "s" : ""} found`}
        </ResultMeta>
      )}

      <Table
        dataSource={alerts}
        columns={columns}
        rowKey="smart_alert_id"
        loading={loading}
        size="small"
        pagination={{ pageSize: 25, showSizeChanger: false, hideOnSinglePage: true }}
        locale={{ emptyText: hasSearched ? "No alerts found" : "Enter filters above and search" }}
        expandable={{
          expandedRowKeys,
          expandedRowRender: (record: AlertItem) => (
            <div style={{ padding: "4px 0 4px 24px" }}>
              <AlertDebugPanel alert={record} />
            </div>
          ),
          rowExpandable: () => true,
          onExpand: (expanded, record) => {
            setExpandedRowKeys(prev =>
              expanded
                ? [...prev, record.smart_alert_id]
                : prev.filter(k => k !== record.smart_alert_id)
            );
          },
          showExpandColumn: false,
        }}
      />
    </PageWrapper>
  );
};
